// Lib
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import quickNotesCanDrop from '../header/workspaceToolsHeader/quickNotes/popup/quickNotesCanDrop';

// Components
import OrchestratedList from '../../list/OrchestratedList';
import QuickNotesEmptyPlaceholder from './QuickNotesEmptyPlaceholder';
import SelectionMarquee from '../../canvas/selection/SelectionMarquee';
import List from '../../list/List';

// Hooks
import { useZoomContext } from '../../canvas/zoom/ZoomStateContext';
import useSelectionMarqueeList from '../../canvas/selection/useSelectionMarqueeList';
import useElementListPlaceholdersInitialisation from '../../element/elementPlaceholder/useElementListPlaceholdersInitialisation';

// Actions
import { refreshIfModified } from '../../element/board/boardRefreshService';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { IN_LIST_MODE } from '../../../common/inList/listTypes';

import './QuickNotesScrollableList.scss';

const INITIAL_RENDER_SIZE = 10;

type Props = {
    scrollableListRef: React.RefObject<HTMLDivElement>;
    quickNotesRootId: string;
    quickNotesChildIds: string[];
    permissions: number;

    gridSize: number;

    listElementProps?: any;
    listZoomScale?: number;

    createShortcutCard?: (element: object) => void;
};

const QuickNotesScrollableList = (props: Props) => {
    const { scrollableListRef, quickNotesChildIds, quickNotesRootId, gridSize, listZoomScale } = props;

    const zoomContextProps = useZoomContext();

    const selectionMarqueeListProps = useSelectionMarqueeList();
    const { setListContainerNode, setSelectionMarqueeActive, listContainerNode } = selectionMarqueeListProps;

    const dispatch = useDispatch();

    const dispatchRefreshIfModified = (quickNotesRootId: string) =>
        dispatch(refreshIfModified({ boardId: quickNotesRootId }));

    useEffect(() => {
        dispatchRefreshIfModified(quickNotesRootId);
    }, []);

    useElementListPlaceholdersInitialisation(quickNotesChildIds);

    return (
        <div className="QuickNotesScrollableList" ref={scrollableListRef} data-scrollable>
            <SelectionMarquee
                sectionElementIds={quickNotesChildIds}
                setSelectionMarqueeActive={setSelectionMarqueeActive}
                eventListenerNode={listContainerNode}
                containerWidth={gridSize * (ELEMENT_DEFAULT_WIDTH + 3.9)}
                gridSize={gridSize}
                disabled={quickNotesChildIds.length === 0}
            />
            <OrchestratedList initialRenderSize={INITIAL_RENDER_SIZE} childElementIds={quickNotesChildIds}>
                {(childElementIds: string[], listSize: number) => (
                    <List
                        {...props}
                        {...zoomContextProps}
                        listContainerRef={setListContainerNode}
                        childElementIds={childElementIds}
                        listSize={listSize}
                        listId={quickNotesRootId}
                        currentBoardId={quickNotesRootId}
                        inListClass={IN_LIST_MODE.IN_LIST_QUICK_NOTES}
                        placeholderComponent={<QuickNotesEmptyPlaceholder />}
                        isEditable
                        listCanDropFn={quickNotesCanDrop}
                        listZoomScale={listZoomScale}
                    />
                )}
            </OrchestratedList>
        </div>
    );
};

export default QuickNotesScrollableList;
